import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Progress } from '../../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Spinner } from '../../components/ui/spinner';
import { Switch } from '../../components/ui/switch';
import { useToast } from '../../hooks/use-toast';
import { formatCurrency } from '../../lib/i18n';
import {
  Upload, FileText, Image, FileSpreadsheet, X, Check, ArrowLeft,
  Sparkles, Pause, ChevronDown, ChevronUp, AlertTriangle, Clock,
  CheckCircle2, XCircle, RotateCcw, Percent, File, Truck, Phone, MapPin
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// File type icons
const FILE_ICONS = {
  csv: FileSpreadsheet,
  excel: FileSpreadsheet,
  pdf: FileText,
  image: Image,
  docx: FileText,
};

// Status badge config
const STATUS_CONFIG = {
  processing: { label: 'Processing', labelBn: 'প্রসেস হচ্ছে', color: 'bg-blue-100 text-blue-700' },
  awaiting_mapping: { label: 'Needs Setup', labelBn: 'সেটআপ দরকার', color: 'bg-yellow-100 text-yellow-700' },
  ready_for_review: { label: 'Ready', labelBn: 'রিভিউ করুন', color: 'bg-green-100 text-green-700' },
  paused: { label: 'Paused', labelBn: 'পজ করা', color: 'bg-orange-100 text-orange-700' },
  completed: { label: 'Done', labelBn: 'সম্পন্ন', color: 'bg-slate-100 text-slate-700' },
  failed: { label: 'Failed', labelBn: 'ব্যর্থ', color: 'bg-red-100 text-red-700' },
  cancelled: { label: 'Cancelled', labelBn: 'বাতিল', color: 'bg-slate-100 text-slate-500' },
};

// Column mapping field options
const FIELD_OPTIONS = [
  { value: 'name', label: 'Part Name', labelBn: 'পার্টের নাম' },
  { value: 'purchase_price', label: 'Purchase Price', labelBn: 'কেনা দাম' },
  { value: 'stock_quantity', label: 'Quantity', labelBn: 'পরিমাণ' },
  { value: 'brand', label: 'Brand', labelBn: 'ব্র্যান্ড' },
  { value: 'sku', label: 'SKU / Part No', labelBn: 'SKU / পার্ট নং' },
  { value: 'category', label: 'Category', labelBn: 'ক্যাটাগরি' },
  { value: '_skip', label: 'Skip', labelBn: 'বাদ দিন' },
];

export default function AIImportView({ onClose, categories, garageSlug, remindersEnabled, consumableCategories }) {
  const { t, language } = useLanguage();
  const { toast } = useToast();
  const bn = language === 'bn';

  // Main flow state
  const [step, setStep] = useState('upload'); // upload | mapping | processing | review | complete
  const [activeBatchId, setActiveBatchId] = useState(null);
  const [batch, setBatch] = useState(null);

  // Upload state
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [creditBalance, setCreditBalance] = useState(0);
  const [estimatedCredits, setEstimatedCredits] = useState(0);
  const [pdfPageCount, setPdfPageCount] = useState(null);
  const fileInputRef = useRef(null);

  // Mapping state (CSV/Excel)
  const [preview, setPreview] = useState(null);
  const [columnMapping, setColumnMapping] = useState({});

  // Review state
  const [items, setItems] = useState([]);
  const [itemsTotal, setItemsTotal] = useState(0);
  const [loadingItems, setLoadingItems] = useState(false);
  const [showMarkupModal, setShowMarkupModal] = useState(false);
  const [markupPercent, setMarkupPercent] = useState('20');
  const [approvingAll, setApprovingAll] = useState(false);

  // History state
  const [showHistory, setShowHistory] = useState(false);
  const [historyBatches, setHistoryBatches] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [rejectedItems, setRejectedItems] = useState([]);
  const [viewingRejectedBatchId, setViewingRejectedBatchId] = useState(null);
  const [loadingRejected, setLoadingRejected] = useState(false);
  const [undoingItemId, setUndoingItemId] = useState(null);

  // Suppliers state
  const [showSuppliers, setShowSuppliers] = useState(false);
  const [supplierContacts, setSupplierContacts] = useState([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);

  // Polling ref
  const pollRef = useRef(null);

  // Load credit balance on mount + warm the backend.
  // Render's free tier spins down when idle and the first request can take 30–60s,
  // which is a common cause of AI scans appearing to "load then fail". Pinging
  // /health on mount means it's usually awake by the time the owner uploads a bill.
  const refreshBalance = useCallback(() => {
    axios.get(`${API}/marketing/credits`).then(r => {
      setCreditBalance(parseInt(r.data?.balance || 0));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    refreshBalance();
    const origin = API.replace(/\/api\/?$/, '');
    axios.get(`${origin}/health`, { timeout: 30000 }).catch(() => {}); // fire-and-forget warm-up
  }, [refreshBalance]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  // ─── Upload ────────────────────────────────────────────────────────

  const getFileCategory = (file) => {
    const type = file.type;
    if (type === 'text/csv' || file.name?.endsWith('.csv')) return 'csv';
    if (type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || file.name?.endsWith('.xlsx')) return 'excel';
    if (type === 'application/pdf') return 'pdf';
    if (type?.startsWith('image/')) return 'image';
    if (type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || file.name?.endsWith('.docx')) return 'docx';
    return null;
  };

  const estimateCredits = (file) => {
    const cat = getFileCategory(file);
    if (cat === 'csv' || cat === 'excel') return 2;
    if (cat === 'pdf' && pdfPageCount) return pdfPageCount * 10;
    return 10; // 1 page for images, or unknown PDF
  };

  // Count PDF pages when a PDF is selected
  const countPdfPages = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const text = new TextDecoder('latin1').decode(data);
      // Count /Type /Page (but not /Type /Pages) — standard PDF page count method
      const matches = text.match(/\/Type\s*\/Page(?!s)/g);
      const pages = matches ? matches.length : 1;
      setPdfPageCount(pages);
      setEstimatedCredits(pages * 10);
    };
    reader.readAsArrayBuffer(file);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const cat = getFileCategory(file);
    if (!cat) {
      toast({ title: bn ? 'এই ফাইল সাপোর্ট করে না' : 'Unsupported file type', variant: 'destructive' });
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast({ title: bn ? 'ফাইলটি অনেক বড় (সর্বোচ্চ ২০MB)' : 'File too large (max 20MB)', variant: 'destructive' });
      return;
    }
    setSelectedFile(file);
    setPdfPageCount(null);
    if (cat === 'pdf') {
      countPdfPages(file);
    } else {
      setEstimatedCredits(cat === 'csv' || cat === 'excel' ? 2 : 10);
    }
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (file) {
      const fakeEvent = { target: { files: [file] } };
      handleFileSelect(fakeEvent);
    }
  };

  const startUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      const res = await axios.post(`${API}/import/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 90000, // allow for large files on slow mobile networks + cold start
      });
      const data = res.data;
      setActiveBatchId(data.batch_id);
      setCreditBalance(prev => prev - data.credits_charged);

      if (data.status === 'awaiting_mapping') {
        // CSV/Excel → go to column mapping step
        const previewRes = await axios.get(`${API}/import/batches/${data.batch_id}/preview`);
        setPreview(previewRes.data);
        setColumnMapping(previewRes.data.suggested_mapping || {});
        setStep('mapping');
      } else {
        // OCR files → go to processing step
        setStep('processing');
        startPolling(data.batch_id);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const isNetwork = !err.response; // timeout / offline / cold-start — no HTTP response
      if (typeof detail === 'object' && detail?.error === 'insufficient_credits') {
        toast({
          title: bn ? 'ক্রেডিট যথেষ্ট নয়' : 'Insufficient credits',
          description: bn
            ? `${detail.needed} ক্রেডিট দরকার, আপনার আছে ${detail.available}`
            : `Need ${detail.needed} credits, you have ${detail.available}`,
          variant: 'destructive',
        });
      } else if (isNetwork) {
        toast({
          title: bn ? 'সংযোগ সমস্যা' : 'Connection problem',
          description: bn
            ? 'সার্ভারে পৌঁছানো যায়নি — ইন্টারনেট দেখে আবার চেষ্টা করুন। কোনো ক্রেডিট কাটা হয়নি।'
            : "Couldn't reach the server — check your internet and try again. No credits were charged.",
          variant: 'destructive',
        });
      } else {
        toast({
          title: bn ? 'আপলোড ব্যর্থ হয়েছে' : 'Upload failed',
          description: typeof detail === 'string' ? detail : (bn ? 'আবার চেষ্টা করুন' : 'Please try again'),
          variant: 'destructive',
        });
      }
      // Keep the selected file so the owner can retry without re-picking it.
    } finally {
      setUploading(false);
    }
  };

  // ─── Column Mapping ────────────────────────────────────────────────

  const submitMapping = async () => {
    // Filter out skipped columns
    const filtered = {};
    for (const [field, col] of Object.entries(columnMapping)) {
      if (field !== '_skip' && col) filtered[field] = col;
    }
    if (!filtered.name) {
      toast({ title: bn ? 'পার্টের নাম সিলেক্ট করুন' : 'Select the Part Name column', variant: 'destructive' });
      return;
    }
    try {
      await axios.post(`${API}/import/batches/${activeBatchId}/mapping`, { mapping: filtered });
      setStep('processing');
      startPolling(activeBatchId);
    } catch (err) {
      toast({ title: bn ? 'ম্যাপিং ব্যর্থ' : 'Mapping failed', variant: 'destructive' });
    }
  };

  // ─── Processing Polling ────────────────────────────────────────────

  const startPolling = (batchId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    let attempts = 0;
    const MAX_ATTEMPTS = 90; // ~3 min at 2s — guards against a stuck/spun-down worker
    pollRef.current = setInterval(async () => {
      attempts += 1;
      try {
        const res = await axios.get(`${API}/import/batches/${batchId}`);
        setBatch(res.data);
        if (res.data.status === 'ready_for_review') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setStep('review');
          loadItems(batchId);
        } else if (res.data.status === 'failed') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          // Failed scans refund the charged credits server-side — reflect that + reassure.
          const refunded = res.data.refunded_credits || res.data.credits_charged || 0;
          refreshBalance();
          const refundNote = refunded
            ? (bn ? ` · ${refunded} ক্রেডিট ফেরত দেওয়া হয়েছে` : ` · ${refunded} credits refunded`)
            : '';
          toast({
            title: bn ? 'স্ক্যান ব্যর্থ হয়েছে' : 'Scan failed',
            description: (res.data.error_message || (bn ? 'আবার চেষ্টা করুন' : 'Please try again')) + refundNote,
            variant: 'destructive',
          });
          setStep('upload'); // keep the selected file so they can retry immediately
        } else if (attempts >= MAX_ATTEMPTS) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          refreshBalance();
          toast({
            title: bn ? 'স্ক্যান শেষ হচ্ছে না' : 'Scan is taking too long',
            description: bn
              ? 'একটু পরে "আগের স্ক্যান" থেকে চেক করুন। ব্যর্থ হলে ক্রেডিট ফেরত পাবেন।'
              : 'Please check "History" again shortly. If it failed, your credits are refunded.',
            variant: 'destructive',
          });
          setStep('upload');
        }
      } catch {
        // Network glitch — keep polling unless we've exceeded the safety cap.
        if (attempts >= MAX_ATTEMPTS) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setStep('upload');
        }
      }
    }, 2000);
  };

  // ─── Review Items ──────────────────────────────────────────────────

  const loadItems = useCallback(async (batchId) => {
    setLoadingItems(true);
    try {
      const [itemsRes, batchRes] = await Promise.all([
        axios.get(`${API}/import/batches/${batchId}/items?status=pending_review&limit=9`),
        axios.get(`${API}/import/batches/${batchId}`),
      ]);
      setItems(itemsRes.data.items || []);
      setItemsTotal(itemsRes.data.total || 0);
      setBatch(batchRes.data);
    } catch {
      toast({ title: bn ? 'লোড ব্যর্থ' : 'Failed to load items', variant: 'destructive' });
    } finally {
      setLoadingItems(false);
    }
  }, [bn, toast]);

  const updateItemField = async (itemId, field, value) => {
    // Optimistic local update
    setItems(prev => prev.map(it =>
      it.id === itemId ? { ...it, final_fields: { ...it.final_fields, [field]: value } } : it
    ));
    try {
      await axios.put(`${API}/import/items/${itemId}`, { [field]: value });
    } catch {
      // Revert on failure would need original value, for now just toast
    }
  };

  const approveItem = async (itemId, updateExisting = false, consumableCategoryId = null) => {
    try {
      const res = await axios.post(`${API}/import/items/${itemId}/approve`, { update_existing: updateExisting });
      // Map consumable category if provided
      const partId = res.data?.part_id;
      if (partId && consumableCategoryId) {
        axios.post(`${API}/marketing/consumable-categories/map-part`, {
          part_id: partId,
          consumable_category_id: consumableCategoryId,
        }).catch(() => {}); // fire and forget
      }
      // Remove from local list with animation
      setItems(prev => prev.filter(it => it.id !== itemId));
      setBatch(prev => prev ? {
        ...prev,
        approved_count: (prev.approved_count || 0) + 1,
        item_counts: {
          ...prev.item_counts,
          approved: (prev.item_counts?.approved || 0) + 1,
          pending_review: Math.max(0, (prev.item_counts?.pending_review || 0) - 1),
        }
      } : prev);
      // Load more items if we're running low
      if (items.length <= 3 && activeBatchId) {
        const res = await axios.get(`${API}/import/batches/${activeBatchId}/items?status=pending_review&limit=9`);
        if (res.data.items?.length > 0) {
          setItems(prev => {
            const existingIds = new Set(prev.map(i => i.id));
            const newItems = res.data.items.filter(i => !existingIds.has(i.id));
            return [...prev, ...newItems];
          });
        }
        setItemsTotal(res.data.total || 0);
      }
      // Check if complete
      if (items.length <= 1) {
        const bRes = await axios.get(`${API}/import/batches/${activeBatchId}`);
        setBatch(bRes.data);
        if (bRes.data.item_counts?.pending_review === 0) {
          setStep('complete');
        }
      }
    } catch (err) {
      const msg = err.response?.data?.detail || (bn ? 'ত্রুটি হয়েছে' : 'Error occurred');
      toast({ title: msg, variant: 'destructive' });
    }
  };

  const rejectItem = async (itemId) => {
    try {
      await axios.post(`${API}/import/items/${itemId}/reject`);
      setItems(prev => prev.filter(it => it.id !== itemId));
      setBatch(prev => prev ? {
        ...prev,
        rejected_count: (prev.rejected_count || 0) + 1,
        item_counts: {
          ...prev.item_counts,
          rejected: (prev.item_counts?.rejected || 0) + 1,
          pending_review: Math.max(0, (prev.item_counts?.pending_review || 0) - 1),
        }
      } : prev);
      if (items.length <= 3 && activeBatchId) {
        const res = await axios.get(`${API}/import/batches/${activeBatchId}/items?status=pending_review&limit=9`);
        if (res.data.items?.length > 0) {
          setItems(prev => {
            const existingIds = new Set(prev.map(i => i.id));
            return [...prev, ...res.data.items.filter(i => !existingIds.has(i.id))];
          });
        }
        setItemsTotal(res.data.total || 0);
      }
      if (items.length <= 1) {
        const bRes = await axios.get(`${API}/import/batches/${activeBatchId}`);
        setBatch(bRes.data);
        if (bRes.data.item_counts?.pending_review === 0) setStep('complete');
      }
    } catch {
      toast({ title: bn ? 'ত্রুটি হয়েছে' : 'Error occurred', variant: 'destructive' });
    }
  };

  // ─── Bulk Actions ──────────────────────────────────────────────────

  const applyMarkup = async () => {
    const pct = parseFloat(markupPercent);
    if (!pct || pct <= 0) return;
    try {
      const res = await axios.post(`${API}/import/batches/${activeBatchId}/apply-markup`, { markup_percentage: pct });
      toast({
        title: bn ? `${res.data.updated}টি পার্টে লাভ সেট হয়েছে` : `Markup applied to ${res.data.updated} parts`,
      });
      setShowMarkupModal(false);
      loadItems(activeBatchId);
    } catch {
      toast({ title: bn ? 'ব্যর্থ হয়েছে' : 'Failed', variant: 'destructive' });
    }
  };

  const approveAll = async () => {
    setApprovingAll(true);
    try {
      const res = await axios.post(`${API}/import/batches/${activeBatchId}/approve-all`);
      toast({
        title: bn
          ? `${res.data.approved}টি পার্ট যোগ হয়েছে`
          : `${res.data.approved} parts added`,
        description: res.data.skipped > 0
          ? (bn ? `${res.data.skipped}টিতে তথ্য অসম্পূর্ণ` : `${res.data.skipped} skipped (incomplete)`)
          : undefined,
      });
      if (res.data.remaining === 0) {
        setStep('complete');
        const bRes = await axios.get(`${API}/import/batches/${activeBatchId}`);
        setBatch(bRes.data);
      } else {
        loadItems(activeBatchId);
      }
    } catch {
      toast({ title: bn ? 'ব্যর্থ হয়েছে' : 'Failed', variant: 'destructive' });
    } finally {
      setApprovingAll(false);
    }
  };

  const pauseBatch = async () => {
    try {
      await axios.post(`${API}/import/batches/${activeBatchId}/pause`);
      toast({ title: bn ? 'সেভ হয়েছে — পরে চালিয়ে যেতে পারবেন' : 'Saved — you can continue later' });
      onClose();
    } catch {
      toast({ title: bn ? 'ব্যর্থ হয়েছে' : 'Failed', variant: 'destructive' });
    }
  };

  // ─── History ───────────────────────────────────────────────────────

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const res = await axios.get(`${API}/import/batches?limit=20`);
      setHistoryBatches(res.data.batches || []);
    } catch {} finally {
      setLoadingHistory(false);
    }
  };

  const loadSuppliers = async () => {
    setLoadingSuppliers(true);
    try {
      const res = await axios.get(`${API}/supplier-contacts`);
      setSupplierContacts(res.data || []);
    } catch {} finally {
      setLoadingSuppliers(false);
    }
  };

  const loadRejectedItems = async (batchId) => {
    setViewingRejectedBatchId(batchId);
    setLoadingRejected(true);
    try {
      const res = await axios.get(`${API}/import/batches/${batchId}/rejected-items`);
      setRejectedItems(res.data.items || []);
    } catch {
      setRejectedItems([]);
    } finally {
      setLoadingRejected(false);
    }
  };

  const undoRejectItem = async (itemId) => {
    setUndoingItemId(itemId);
    try {
      await axios.post(`${API}/import/items/${itemId}/undo-reject`);
      setRejectedItems(prev => prev.filter(it => it.id !== itemId));
      toast({ title: bn ? 'পার্ট পুনরুদ্ধার হয়েছে — রিভিউ করুন' : 'Part restored — ready for review' });
      // Refresh history to update counts
      loadHistory();
    } catch {
      toast({ title: bn ? 'ব্যর্থ হয়েছে' : 'Failed', variant: 'destructive' });
    } finally {
      setUndoingItemId(null);
    }
  };

  const resumeBatch = async (batchId) => {
    try {
      const b = historyBatches.find(b => b.id === batchId);
      if (b?.status === 'paused') {
        await axios.post(`${API}/import/batches/${batchId}/resume`);
      }
      setActiveBatchId(batchId);
      setStep('review');
      setShowHistory(false);
      loadItems(batchId);
    } catch {
      toast({ title: bn ? 'ব্যর্থ হয়েছে' : 'Failed', variant: 'destructive' });
    }
  };

  // ─── Render Helpers ────────────────────────────────────────────────

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const confidenceDot = (score) => {
    if (!score && score !== 0) return null;
    const color = score >= 0.8 ? 'bg-green-500' : score >= 0.5 ? 'bg-yellow-500' : 'bg-red-400';
    return <span className={`inline-block w-1.5 h-1.5 rounded-full ${color} ml-1`} />;
  };

  // ═══════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════

  // ── UPLOAD STEP ────────────────────────────────────────────────────
  if (step === 'upload') {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={onClose} className="shrink-0">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h2 className="text-lg sm:text-xl font-bold font-['Barlow_Condensed'] flex items-center gap-2">
                <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                {bn ? 'স্মার্ট ইমপোর্ট' : 'Smart Import'}
              </h2>
              <p className="text-xs sm:text-sm text-slate-500">
                {bn ? 'সাপ্লায়ারের বিল থেকে পার্ট যোগ করুন' : 'Add parts from supplier bills'}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => { setShowSuppliers(true); loadSuppliers(); }}>
              <Truck className="w-4 h-4 mr-1" />
              {bn ? 'সাপ্লায়ার' : 'Suppliers'}
            </Button>
            <Button variant="outline" size="sm" onClick={() => { setShowHistory(true); loadHistory(); }}>
              <Clock className="w-4 h-4 mr-1" />
              {bn ? 'আগের স্ক্যান' : 'History'}
            </Button>
          </div>
        </div>

        {/* Drop Zone */}
        <div
          className="border-2 border-dashed border-slate-300 rounded-xl p-8 sm:p-12 text-center hover:border-blue-400 hover:bg-blue-50/30 transition-all cursor-pointer"
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleFileDrop}
        >
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept=".csv,.xlsx,.pdf,.jpg,.jpeg,.png,.webp,.docx"
            onChange={handleFileSelect}
          />
          <motion.div
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ repeat: Infinity, duration: 3, ease: 'easeInOut' }}
            className="inline-block"
          >
            <Upload className="w-12 h-12 text-blue-400 mx-auto mb-4" />
          </motion.div>
          <p className="text-sm sm:text-base font-semibold text-slate-700 mb-2">
            {bn ? 'আপনার সাপ্লায়ারের বিল আপলোড করুন' : 'Upload your supplier bill'}
          </p>
          <p className="text-xs sm:text-sm text-slate-500 mb-4">
            {bn ? 'ছবি, পিডিএফ, এক্সেল — যেকোনো ফরম্যাট' : 'Photo, PDF, Excel — any format'}
          </p>
          <div className="flex justify-center gap-4 text-slate-400">
            <div className="flex flex-col items-center gap-1">
              <Image className="w-6 h-6" />
              <span className="text-xs">{bn ? 'ছবি' : 'Photo'}</span>
            </div>
            <div className="flex flex-col items-center gap-1">
              <FileText className="w-6 h-6" />
              <span className="text-xs">PDF</span>
            </div>
            <div className="flex flex-col items-center gap-1">
              <FileSpreadsheet className="w-6 h-6" />
              <span className="text-xs">Excel</span>
            </div>
            <div className="flex flex-col items-center gap-1">
              <File className="w-6 h-6" />
              <span className="text-xs">Word</span>
            </div>
          </div>
        </div>

        {/* How It Works — Step Infographics */}
        {!selectedFile && (
          <div className="mt-2">
            <div className="flex items-center justify-center gap-2 mb-5">
              <div className="h-px flex-1 max-w-[60px] bg-slate-200" />
              <p className="text-xs sm:text-sm font-semibold text-slate-400 uppercase tracking-wider">
                {bn ? 'কিভাবে কাজ করে' : 'How It Works'}
              </p>
              <div className="h-px flex-1 max-w-[60px] bg-slate-200" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 sm:gap-6">
              {[
                {
                  step: 1,
                  icon: <Upload className="w-5 h-5" />,
                  titleEn: 'Upload Bill',
                  titleBn: 'বিল আপলোড',
                  descEn: 'Snap a photo or upload PDF/Excel of your supplier bill',
                  descBn: 'সাপ্লায়ারের বিলের ছবি তুলুন বা PDF/Excel আপলোড করুন',
                  iconBg: 'bg-blue-600',
                  delay: 0,
                },
                {
                  step: 2,
                  icon: <Sparkles className="w-5 h-5" />,
                  titleEn: 'AI Extracts Data',
                  titleBn: 'AI ডেটা বের করে',
                  descEn: 'Part names, prices & quantities — extracted in seconds',
                  descBn: 'পার্টের নাম, দাম ও পরিমাণ — সেকেন্ডে বের হয়ে যায়',
                  iconBg: 'bg-purple-600',
                  delay: 0.15,
                },
                {
                  step: 3,
                  icon: <CheckCircle2 className="w-5 h-5" />,
                  titleEn: 'Review & Add',
                  titleBn: 'দেখুন ও যোগ করুন',
                  descEn: 'Set your selling price and add to inventory with one tap',
                  descBn: 'বিক্রি দাম দিন, এক ট্যাপে ইনভেন্টরিতে যোগ করুন',
                  iconBg: 'bg-green-600',
                  delay: 0.3,
                },
              ].map((s) => (
                <motion.div
                  key={s.step}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: s.delay + 0.2, duration: 0.5, ease: 'easeOut' }}
                  className="flex flex-col items-center text-center px-3"
                >
                  {/* Step icon circle */}
                  <div className="relative mb-3">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: s.delay + 0.3, type: 'spring', stiffness: 260, damping: 20 }}
                      className={`w-11 h-11 rounded-xl ${s.iconBg} text-white flex items-center justify-center shadow-sm`}
                    >
                      {s.icon}
                    </motion.div>
                    <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-slate-800 text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-white">
                      {s.step}
                    </span>
                  </div>
                  <p className="text-sm font-semibold text-slate-700 mb-0.5">
                    {bn ? s.titleBn : s.titleEn}
                  </p>
                  <p className="text-[11px] sm:text-xs text-slate-400 leading-relaxed max-w-[200px]">
                    {bn ? s.descBn : s.descEn}
                  </p>
                </motion.div>
              ))}
            </div>
            {/* Connecting line for desktop */}
            <div className="hidden sm:flex justify-center mt-4">
              <motion.div
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ delay: 0.8, duration: 0.5 }}
                className="h-px bg-gradient-to-r from-blue-300 via-purple-300 to-green-300 w-full max-w-sm origin-left"
              />
            </div>
            {/* Tagline */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2 }}
              className="text-center text-[11px] text-slate-400 mt-3 flex items-center justify-center gap-1.5"
            >
              <Sparkles className="w-3 h-3" />
              {bn ? 'হাতে লেখার দরকার নেই — AI করে দেবে সব' : 'No more manual entry — Let AI do the heavy lifting'}
            </motion.p>
          </div>
        )}

        {/* Selected File Preview */}
        <AnimatePresence>
          {selectedFile && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 mb-4">
                    {React.createElement(FILE_ICONS[getFileCategory(selectedFile)] || FileText, { className: 'w-8 h-8 text-blue-500' })}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{selectedFile.name}</p>
                      <p className="text-sm text-slate-500">{formatFileSize(selectedFile.size)}</p>
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => setSelectedFile(null)}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>

                  <div className="flex items-center justify-between bg-slate-50 rounded-lg p-3 mb-4">
                    <div>
                      <p className="text-[10px] sm:text-xs text-slate-600">
                        {bn ? 'স্ক্যান খরচ' : 'Scan cost'}
                      </p>
                      <p className="text-sm sm:text-base font-bold text-blue-700">
                        {estimateCredits(selectedFile)} {bn ? 'ক্রেডিট' : 'credits'}
                      </p>
                      {getFileCategory(selectedFile) === 'pdf' && pdfPageCount && pdfPageCount > 1 && (
                        <p className="text-[10px] text-slate-500">
                          {pdfPageCount} {bn ? 'পেজ' : 'pages'} x 10
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] sm:text-xs text-slate-600">
                        {bn ? 'আপনার ব্যালেন্স' : 'Your balance'}
                      </p>
                      <p className={`text-sm sm:text-base font-bold ${creditBalance >= estimateCredits(selectedFile) ? 'text-green-600' : 'text-red-600'}`}>
                        {creditBalance} {bn ? 'ক্রেডিট' : 'credits'}
                      </p>
                    </div>
                  </div>

                  {creditBalance < estimateCredits(selectedFile) ? (
                    <div className="text-center">
                      <p className="text-red-600 text-sm mb-2">
                        {bn ? 'আপনার ক্রেডিট যথেষ্ট নয়' : 'Not enough credits'}
                      </p>
                      <Button
                        variant="outline"
                        className="border-blue-600 text-blue-600"
                        onClick={() => {
                          window.location.href = `/${garageSlug}/admin/marketing?tab=credits`;
                        }}
                      >
                        {bn ? 'ক্রেডিট কিনুন' : 'Buy Credits'}
                      </Button>
                    </div>
                  ) : (
                    <Button
                      className="w-full gap-2 bg-green-600 hover:bg-green-700 text-white h-10 sm:h-12 text-xs sm:text-sm"
                      onClick={startUpload}
                      disabled={uploading}
                    >
                      {uploading ? <Spinner size="sm" /> : <Sparkles className="w-5 h-5" />}
                      {bn ? 'স্ক্যান শুরু করুন' : 'Start Scan'}
                    </Button>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>

        {/* History Dialog */}
        <Dialog open={showHistory} onOpenChange={(open) => { setShowHistory(open); if (!open) { setViewingRejectedBatchId(null); setRejectedItems([]); } }}>
          <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {viewingRejectedBatchId
                  ? (bn ? 'বাদ দেওয়া পার্ট' : 'Rejected Parts')
                  : (bn ? 'আগের স্ক্যান' : 'Scan History')}
              </DialogTitle>
            </DialogHeader>

            {/* Rejected Items Sub-View */}
            {viewingRejectedBatchId ? (
              <div className="space-y-3">
                <Button variant="ghost" size="sm" onClick={() => { setViewingRejectedBatchId(null); setRejectedItems([]); }}>
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  {bn ? 'ফিরে যান' : 'Back'}
                </Button>
                {loadingRejected ? (
                  <div className="flex justify-center py-8"><Spinner size="lg" /></div>
                ) : rejectedItems.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">
                    {bn ? 'কোনো বাদ দেওয়া পার্ট নেই' : 'No rejected parts'}
                  </p>
                ) : (
                  <div className="space-y-2">
                    {rejectedItems.map(item => {
                      const fields = item.final_fields || item.detected_fields || {};
                      return (
                        <div key={item.id} className="flex items-center gap-3 p-3 bg-red-50 border border-red-100 rounded-lg">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{fields.name || '—'}</p>
                            <div className="flex items-center gap-2 text-xs text-slate-500">
                              {fields.category && <span className="capitalize">{fields.category}</span>}
                              {fields.purchase_price && <span>৳{fields.purchase_price}</span>}
                              {fields.stock_quantity && <span>×{fields.stock_quantity}</span>}
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-green-600 text-green-700 hover:bg-green-50 shrink-0"
                            onClick={() => undoRejectItem(item.id)}
                            disabled={undoingItemId === item.id}
                          >
                            {undoingItemId === item.id ? <Spinner size="sm" /> : <RotateCcw className="w-3.5 h-3.5 mr-1" />}
                            {bn ? 'ফেরত আনুন' : 'Undo'}
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
            /* History List */
            loadingHistory ? (
              <div className="flex justify-center py-8"><Spinner size="lg" /></div>
            ) : historyBatches.length === 0 ? (
              <p className="text-center text-slate-500 py-8">
                {bn ? 'কোনো স্ক্যান নেই' : 'No scans yet'}
              </p>
            ) : (
              <div className="space-y-3">
                {historyBatches.map(b => {
                  const sc = STATUS_CONFIG[b.status] || STATUS_CONFIG.completed;
                  const canResume = ['ready_for_review', 'paused'].includes(b.status);
                  return (
                    <div key={b.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                      {React.createElement(FILE_ICONS[b.file_category] || FileText, { className: 'w-6 h-6 text-slate-400 shrink-0' })}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{b.file_name}</p>
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          <Badge className={`text-[10px] ${sc.color}`}>
                            {bn ? sc.labelBn : sc.label}
                          </Badge>
                          <span>
                            {b.approved_count > 0 && <span className="flex items-center gap-0.5"><CheckCircle2 className="w-3 h-3 text-green-600" />{b.approved_count}</span>}
                            {b.rejected_count > 0 && (
                              <button
                                className="flex items-center gap-0.5 hover:underline cursor-pointer"
                                onClick={() => loadRejectedItems(b.id)}
                              >
                                <XCircle className="w-3 h-3 text-red-500" />{b.rejected_count}
                              </button>
                            )}
                          </span>
                          <span>{new Date(b.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <div className="flex gap-1.5 shrink-0">
                        {b.rejected_count > 0 && (
                          <Button size="sm" variant="ghost" className="text-xs text-red-600 hover:bg-red-50 px-2" onClick={() => loadRejectedItems(b.id)}>
                            <RotateCcw className="w-3.5 h-3.5 mr-1" />
                            {bn ? 'ফেরত' : 'Undo'}
                          </Button>
                        )}
                        {canResume && (
                          <Button size="sm" variant="outline" onClick={() => resumeBatch(b.id)}>
                            {bn ? 'চালিয়ে যান' : 'Continue'}
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )
            )}
          </DialogContent>
        </Dialog>

        {/* Suppliers Dialog */}
        <Dialog open={showSuppliers} onOpenChange={setShowSuppliers}>
          <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{bn ? 'সাপ্লায়ার তালিকা' : 'Supplier Contacts'}</DialogTitle>
            </DialogHeader>
            {loadingSuppliers ? (
              <div className="flex justify-center py-8"><Spinner size="lg" /></div>
            ) : supplierContacts.length === 0 ? (
              <p className="text-center text-slate-500 py-8">
                {bn ? 'কোনো সাপ্লায়ার পাওয়া যায়নি। AI ইমপোর্ট থেকে স্বয়ংক্রিয়ভাবে সংগ্রহ হবে।' : 'No suppliers yet. They are auto-detected from your AI imports.'}
              </p>
            ) : (
              <div className="space-y-3">
                {supplierContacts.map(sup => (
                  <div key={sup.id || sup.name} className="p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center shrink-0">
                        <Truck className="w-4 h-4 text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold">{sup.name}</p>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500 mt-0.5">
                          {sup.phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />{sup.phone}
                            </span>
                          )}
                          {sup.address && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />{sup.address}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-xs text-slate-500">{sup.import_count || 0} {bn ? 'ইমপোর্ট' : 'imports'}</p>
                        {sup.last_seen_at && (
                          <p className="text-[10px] text-slate-400">{new Date(sup.last_seen_at).toLocaleDateString()}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ── COLUMN MAPPING STEP ────────────────────────────────────────────
  if (step === 'mapping' && preview) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => { setStep('upload'); setPreview(null); }}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h2 className="text-lg sm:text-xl font-bold font-['Barlow_Condensed']">
              {bn ? 'কলাম সিলেক্ট করুন' : 'Map Your Columns'}
            </h2>
            <p className="text-xs sm:text-sm text-slate-500">
              {bn ? 'প্রতিটি কলামে কী আছে সিলেক্ট করুন' : 'Tell us what each column contains'}
            </p>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm border rounded-lg overflow-hidden">
            <thead>
              <tr>
                {preview.headers.map((h, i) => (
                  <th key={i} className="p-2 bg-slate-50 border-b min-w-[120px]">
                    <Select
                      value={Object.entries(columnMapping).find(([, v]) => v === h)?.[0] || '_skip'}
                      onValueChange={(val) => {
                        setColumnMapping(prev => {
                          const next = { ...prev };
                          // Remove any existing mapping pointing to this header
                          for (const [k, v] of Object.entries(next)) {
                            if (v === h) delete next[k];
                          }
                          if (val !== '_skip') next[val] = h;
                          return next;
                        });
                      }}
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue placeholder={bn ? 'সিলেক্ট করুন' : 'Select...'} />
                      </SelectTrigger>
                      <SelectContent>
                        {FIELD_OPTIONS.map(f => (
                          <SelectItem key={f.value} value={f.value}>
                            {bn ? f.labelBn : f.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500 mt-1 font-normal truncate">{h}</p>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {preview.preview_rows.map((row, i) => (
                <tr key={i} className="border-b">
                  {row.map((cell, j) => (
                    <td key={j} className="p-2 text-xs text-slate-600 truncate max-w-[150px]">{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Button
          className="w-full h-10 sm:h-12 bg-green-600 hover:bg-green-700 text-white text-xs sm:text-sm gap-2"
          onClick={submitMapping}
        >
          <Check className="w-5 h-5" />
          {bn ? 'শুরু করুন' : 'Continue'}
        </Button>
      </div>
    );
  }

  // ── PROCESSING STEP ────────────────────────────────────────────────
  if (step === 'processing') {
    const pages = batch?.page_count || 1;
    const processed = batch?.processed_pages || 0;
    const progress = pages > 0 ? Math.round((processed / pages) * 100) : 0;

    return (
      <div className="flex flex-col items-center justify-center py-16 space-y-6">
        {/* Scanning animation */}
        <motion.div
          animate={{ y: [0, -8, 0] }}
          transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
          className="relative"
        >
          <FileText className="w-20 h-20 text-blue-300" />
          <motion.div
            className="absolute left-0 right-0 h-0.5 bg-blue-600"
            animate={{ top: ['20%', '80%', '20%'] }}
            transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
          />
        </motion.div>

        <div className="text-center">
          <h3 className="text-sm sm:text-base font-semibold text-slate-700 mb-1">
            {bn ? 'আপনার বিল স্ক্যান হচ্ছে...' : 'Scanning your bill...'}
          </h3>
          {pages > 1 && (
            <p className="text-sm text-slate-500">
              {bn ? `পেজ ${processed}/${pages} স্ক্যান হচ্ছে` : `Scanning page ${processed} of ${pages}`}
            </p>
          )}
        </div>

        <div className="w-64">
          <Progress value={progress || 10} className="h-2" />
        </div>

        <p className="text-xs text-slate-400">
          {bn ? 'এটি কয়েক সেকেন্ড সময় নিতে পারে' : 'This may take a few seconds'}
        </p>
      </div>
    );
  }

  // ── REVIEW STEP ────────────────────────────────────────────────────
  if (step === 'review') {
    const counts = batch?.item_counts || {};
    const totalReviewed = (counts.approved || 0) + (counts.rejected || 0);
    const totalItems = counts.total || batch?.total_items || 0;
    const progressPct = totalItems > 0 ? Math.round((totalReviewed / totalItems) * 100) : 0;

    return (
      <div className="space-y-4">
        {/* Review Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={onClose}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h2 className="text-lg sm:text-xl font-bold font-['Barlow_Condensed']">
                {bn ? 'পার্ট রিভিউ করুন' : 'Review Parts'}
              </h2>
              <p className="text-xs sm:text-sm text-slate-500 truncate">{batch?.file_name}</p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {/* Progress pill */}
            <div className="flex items-center gap-2 bg-slate-100 rounded-full px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm">
              <span className="text-green-600 font-semibold flex items-center gap-0.5"><CheckCircle2 className="w-3.5 h-3.5" /> {counts.approved || 0}</span>
              <span className="text-red-500 font-semibold flex items-center gap-0.5"><XCircle className="w-3.5 h-3.5" /> {counts.rejected || 0}</span>
              <span className="text-slate-500 flex items-center gap-0.5"><Clock className="w-3.5 h-3.5" /> {counts.pending_review || 0}</span>
            </div>

            {/* Bulk markup button */}
            <Button variant="outline" size="sm" className="text-xs sm:text-sm" onClick={() => setShowMarkupModal(true)}>
              <Percent className="w-3.5 h-3.5 mr-1" />
              {bn ? 'লাভ সেট করুন' : 'Set Markup'}
            </Button>

            {/* Approve all */}
            <Button
              variant="outline" size="sm"
              className="border-green-600 text-green-700 hover:bg-green-50 text-xs sm:text-sm"
              onClick={approveAll}
              disabled={approvingAll}
            >
              {approvingAll ? <Spinner size="sm" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />}
              {bn ? 'সব যোগ করুন' : 'Add All'}
            </Button>

            {/* Pause */}
            <Button variant="ghost" size="sm" className="text-xs sm:text-sm" onClick={pauseBatch}>
              <Pause className="w-3.5 h-3.5 mr-1" />
              {bn ? 'পরে করবো' : 'Later'}
            </Button>
          </div>
        </div>

        {/* Supplier info banner (if extracted) */}
        {batch?.supplier_info?.name && (
          <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-lg px-3 sm:px-4 py-2 text-xs sm:text-sm">
            <span className="font-medium text-blue-800">{batch.supplier_info.name}</span>
            {batch.supplier_info.phone && <span className="text-blue-600">{batch.supplier_info.phone}</span>}
            {batch.supplier_info.bill_no && <span className="text-blue-500">#{batch.supplier_info.bill_no}</span>}
          </div>
        )}

        {/* Progress bar */}
        <Progress value={progressPct} className="h-1.5" />

        {/* Cards Grid */}
        {loadingItems ? (
          <div className="flex justify-center py-12"><Spinner size="lg" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            {bn ? 'কোনো পার্ট পাওয়া যায়নি' : 'No items to review'}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            <AnimatePresence mode="popLayout">
              {items.map((item, idx) => (
                <ReviewCard
                  key={item.id}
                  item={item}
                  bn={bn}
                  categories={categories}
                  remindersEnabled={remindersEnabled}
                  consumableCategories={consumableCategories}
                  onUpdateField={updateItemField}
                  onApprove={approveItem}
                  onReject={rejectItem}
                  autoFocusSellingPrice={idx === 0}
                />
              ))}
            </AnimatePresence>
          </div>
        )}

        {/* Markup Modal */}
        <Dialog open={showMarkupModal} onOpenChange={setShowMarkupModal}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>{bn ? 'সব পার্টে লাভ সেট করুন' : 'Set Profit Markup'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>{bn ? 'লাভের শতাংশ' : 'Markup Percentage'}</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    type="number"
                    value={markupPercent}
                    onChange={e => setMarkupPercent(e.target.value)}
                    className="text-lg font-bold"
                    min="1" max="500"
                  />
                  <span className="text-2xl font-bold text-slate-400">%</span>
                </div>
              </div>
              <div className="bg-blue-50 rounded-lg p-3 text-sm">
                <p className="text-slate-600">
                  {bn ? 'উদাহরণ:' : 'Example:'}
                </p>
                <p className="font-semibold">
                  {bn ? 'কেনা' : 'Buy'} ৳100 → {bn ? 'বিক্রি' : 'Sell'} ৳{Math.round(100 * (1 + (parseFloat(markupPercent) || 0) / 100))}
                </p>
              </div>
              <Button
                className="w-full bg-blue-700 hover:bg-blue-800 text-white"
                onClick={applyMarkup}
              >
                {bn ? 'প্রয়োগ করুন' : 'Apply to All'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ── COMPLETE STEP ──────────────────────────────────────────────────
  if (step === 'complete') {
    return (
      <div className="flex flex-col items-center justify-center py-16 space-y-6">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 200, damping: 15 }}
        >
          <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
            <CheckCircle2 className="w-10 h-10 text-green-600" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="text-center"
        >
          <h2 className="text-lg sm:text-2xl font-bold text-slate-800 mb-2">
            {bn ? 'ইমপোর্ট সম্পন্ন!' : 'Import Complete!'}
          </h2>
          <div className="flex justify-center gap-6 mb-6">
            <div className="text-center">
              <p className="text-lg sm:text-2xl font-bold text-green-600">{batch?.item_counts?.approved || batch?.approved_count || 0}</p>
              <p className="text-xs sm:text-sm text-slate-500">{bn ? 'পার্ট যোগ হয়েছে' : 'Parts Added'}</p>
            </div>
            <div className="text-center">
              <p className="text-lg sm:text-2xl font-bold text-red-400">{batch?.item_counts?.rejected || batch?.rejected_count || 0}</p>
              <p className="text-xs sm:text-sm text-slate-500">{bn ? 'বাদ দেওয়া হয়েছে' : 'Skipped'}</p>
            </div>
          </div>
        </motion.div>

        <div className="flex gap-3">
          <Button
            className="bg-blue-800 hover:bg-blue-900 text-white h-10 sm:h-12 px-4 sm:px-6 text-xs sm:text-sm"
            onClick={onClose}
          >
            {bn ? 'ইনভেন্টরি দেখুন' : 'View Inventory'}
          </Button>
          <Button
            variant="outline"
            className="h-10 sm:h-12 px-4 sm:px-6 text-xs sm:text-sm"
            onClick={() => {
              setStep('upload');
              setSelectedFile(null);
              setActiveBatchId(null);
              setBatch(null);
              setItems([]);
            }}
          >
            {bn ? 'আরেকটি ফাইল স্ক্যান' : 'Scan Another File'}
          </Button>
        </div>
      </div>
    );
  }

  return null;
}


// ═══════════════════════════════════════════════════════════════════════
// Review Card Component
// ═══════════════════════════════════════════════════════════════════════

function ReviewCard({ item, bn, categories, remindersEnabled, consumableCategories, onUpdateField, onApprove, onReject, autoFocusSellingPrice }) {
  const [expanded, setExpanded] = useState(false);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [consumableCategoryId, setConsumableCategoryId] = useState('');
  const [isConsumable, setIsConsumable] = useState(true);
  const sellingPriceRef = useRef(null);
  const addBtnRef = useRef(null);
  const [consumableSuggest, setConsumableSuggest] = useState(null);
  const [existingPart, setExistingPart] = useState(null);
  const [loadingExisting, setLoadingExisting] = useState(false);

  // Auto-focus selling price on first card
  useEffect(() => {
    if (autoFocusSellingPrice && sellingPriceRef.current) {
      setTimeout(() => sellingPriceRef.current?.focus(), 150);
    }
  }, [autoFocusSellingPrice]);

  const handleSellingPriceKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (addBtnRef.current && !addBtnRef.current.disabled) {
        addBtnRef.current.focus();
      }
    }
  };

  const handleAddBtnKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleApprove();
    }
  };

  const fields = item.final_fields || item.detected_fields || {};
  const scores = item.confidence_scores || {};
  const dup = item.duplicate_match;

  // For update mode: use existing part's sell price if buy price matches
  const existBuyPrice = existingPart?.purchase_price || 0;
  const existSellPrice = existingPart?.selling_price || 0;
  const newBuyPrice = fields.purchase_price || 0;
  const buyPriceSame = existingPart && Math.abs(existBuyPrice - newBuyPrice) < 1;
  const categoryDiffers = existingPart && fields.category && existingPart.category !== fields.category;

  // Auto-fill sell price from existing when buy price matches and sell price not set
  useEffect(() => {
    if (updateExisting && buyPriceSame && !fields.selling_price && existSellPrice > 0) {
      onUpdateField(item.id, 'selling_price', existSellPrice);
    }
  }, [updateExisting, buyPriceSame, existSellPrice, fields.selling_price]);

  const hasRequired = updateExisting
    ? (fields.purchase_price && fields.selling_price)
    : (fields.name && fields.category && fields.purchase_price && fields.selling_price);

  // Calculate margin if both prices set
  const profit = (fields.selling_price || 0) - (fields.purchase_price || 0);
  const profitMargin = fields.purchase_price > 0 ? ((profit / fields.purchase_price) * 100).toFixed(0) : 0;

  // Fetch existing part data when "Update Stock" is toggled on
  useEffect(() => {
    if (!updateExisting || !dup?.part_id || existingPart) return;
    setLoadingExisting(true);
    axios.get(`${API}/parts/${dup.part_id}`).then(r => {
      setExistingPart(r.data);
    }).catch(() => {}).finally(() => setLoadingExisting(false));
  }, [updateExisting, dup?.part_id, existingPart]);

  // Auto-classify consumable category when name or category changes
  useEffect(() => {
    if (!remindersEnabled || !fields.name || fields.name.length < 3) return;
    const timer = setTimeout(async () => {
      try {
        const { data } = await axios.post(`${API}/marketing/consumable-categories/classify-single`, {
          part_name: fields.name,
          category: fields.category || '',
        });
        if (data.confidence === 'high' || data.confidence === 'medium') {
          setConsumableSuggest(data);
          const suggestedCat = consumableCategories.find(c => c.id === data.suggested_category_id);
          if (suggestedCat?.is_non_consumable) {
            setIsConsumable(false);
            setConsumableCategoryId(suggestedCat.id);
          } else if (data.suggested_category_id) {
            setIsConsumable(true);
            setConsumableCategoryId(data.suggested_category_id);
          }
        }
      } catch { /* ignore */ }
    }, 400);
    return () => clearTimeout(timer);
  }, [fields.name, fields.category, remindersEnabled, consumableCategories]);

  const handleApprove = async () => {
    setApproving(true);
    await onApprove(item.id, updateExisting, isConsumable ? consumableCategoryId : null);
    setApproving(false);
  };

  const handleReject = async () => {
    setRejecting(true);
    await onReject(item.id);
    setRejecting(false);
  };

  const confidenceDot = (field) => {
    const s = scores[field];
    if (!s && s !== 0) return null;
    const color = s >= 0.8 ? 'bg-green-500' : s >= 0.5 ? 'bg-yellow-500' : 'bg-red-400';
    return <span className={`inline-block w-1.5 h-1.5 rounded-full ${color} ml-1`} />;
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: 80 }}
      animate={{ opacity: 1, x: 0 }}
      exit={approving
        ? { opacity: 0, x: -200, scale: 0.8, transition: { duration: 0.4 } }
        : { opacity: 0, y: 30, scale: 0.95, transition: { duration: 0.3 } }
      }
      transition={{ type: 'spring', stiffness: 300, damping: 25 }}
    >
      <Card className={`overflow-hidden transition-shadow hover:shadow-md ${approving ? 'ring-2 ring-green-400' : rejecting ? 'ring-2 ring-red-400' : ''}`}>
        <CardContent className="p-3 sm:p-5">
          {/* Duplicate Warning + Update Stock Comparison */}
          {dup && (
            <div className="mb-2 sm:mb-3 px-2 py-1.5 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-center gap-1.5 text-[10px] sm:text-xs text-amber-700 mb-1">
                <AlertTriangle className="w-3 h-3 shrink-0" />
                <span className="font-medium truncate">{bn ? 'একই নামের পার্ট আছে:' : 'Similar part exists:'} {dup.name}</span>
              </div>
              <div className="flex gap-2 mb-1.5">
                <button
                  className={`text-[10px] sm:text-xs px-2 py-0.5 rounded-md transition-colors ${!updateExisting ? 'bg-amber-200 text-amber-800 font-medium' : 'bg-white text-slate-600 border'}`}
                  onClick={() => setUpdateExisting(false)}
                >{bn ? 'নতুন পার্ট' : 'Add New'}</button>
                <button
                  className={`text-[10px] sm:text-xs px-2 py-0.5 rounded-md transition-colors ${updateExisting ? 'bg-amber-200 text-amber-800 font-medium' : 'bg-white text-slate-600 border'}`}
                  onClick={() => setUpdateExisting(true)}
                >{bn ? 'স্টক আপডেট' : 'Update Stock'}</button>
              </div>

              {/* Smart comparison view when Update Stock mode */}
              {updateExisting && (
                <div className="mt-1.5 pt-1.5 border-t border-amber-200">
                  {loadingExisting ? (
                    <div className="flex items-center gap-1.5 text-[10px] sm:text-xs text-amber-600"><Spinner size="sm" /> {bn ? 'লোড হচ্ছে...' : 'Loading...'}</div>
                  ) : existingPart ? (
                    <div className="space-y-2">
                      {/* Stock Math: existing + new = total */}
                      <div className="flex items-center gap-2 p-1.5 bg-white rounded border border-amber-100">
                        <div className="text-center flex-1">
                          <p className="text-[9px] text-amber-600 uppercase">{bn ? 'বর্তমান' : 'Current'}</p>
                          <p className="text-sm sm:text-base font-bold text-slate-700">{existingPart.stock_quantity || 0}</p>
                        </div>
                        <span className="text-amber-400 font-bold">+</span>
                        <div className="text-center flex-1">
                          <p className="text-[9px] text-amber-600 uppercase">{bn ? 'নতুন' : 'New'}</p>
                          <Input
                            type="number"
                            value={fields.stock_quantity || ''}
                            onChange={e => onUpdateField(item.id, 'stock_quantity', parseInt(e.target.value) || 0)}
                            className="h-7 w-14 text-sm sm:text-base font-bold text-center border-dashed mx-auto"
                          />
                        </div>
                        <span className="text-amber-400 font-bold">=</span>
                        <div className="text-center flex-1">
                          <p className="text-[9px] text-green-600 uppercase">{bn ? 'মোট' : 'Total'}</p>
                          <p className="text-sm sm:text-base font-bold text-green-600">{(existingPart.stock_quantity || 0) + (fields.stock_quantity || 0)}</p>
                        </div>
                      </div>

                      {/* Price comparison: existing vs new */}
                      <div className="grid grid-cols-2 gap-1.5">
                        {/* Buy Price comparison */}
                        <div className={`p-1.5 rounded border ${buyPriceSame ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200'}`}>
                          <p className="text-[9px] uppercase text-slate-500 mb-0.5">{bn ? 'কেনা দাম' : 'Buy Price'}</p>
                          <div className="flex items-center gap-1">
                            <span className="text-[10px] text-slate-400 line-through">৳{existBuyPrice}</span>
                            {!buyPriceSame && <span className="text-[10px] text-orange-500">→</span>}
                          </div>
                          <div className="relative mt-0.5">
                            <span className="absolute left-0 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">৳</span>
                            <Input
                              type="number"
                              value={fields.purchase_price || ''}
                              onChange={e => onUpdateField(item.id, 'purchase_price', parseFloat(e.target.value) || 0)}
                              className="h-6 text-xs sm:text-sm font-semibold border-0 border-b border-dashed rounded-none pl-3 pr-0 focus-visible:ring-0"
                            />
                          </div>
                        </div>

                        {/* Sell Price comparison */}
                        <div className={`p-1.5 rounded border ${fields.selling_price === existSellPrice ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200'}`}>
                          <p className="text-[9px] uppercase text-slate-500 mb-0.5">{bn ? 'বিক্রি দাম' : 'Sell Price'}</p>
                          <div className="flex items-center gap-1">
                            <span className="text-[10px] text-slate-400">{bn ? 'আগের' : 'Was'}: ৳{existSellPrice}</span>
                          </div>
                          <div className="relative mt-0.5">
                            <span className="absolute left-0 top-1/2 -translate-y-1/2 text-[10px] text-green-500">৳</span>
                            <Input
                              ref={sellingPriceRef}
                              type="number"
                              value={fields.selling_price || ''}
                              onChange={e => onUpdateField(item.id, 'selling_price', parseFloat(e.target.value) || 0)}
                              onKeyDown={handleSellingPriceKeyDown}
                              className="h-6 text-xs sm:text-sm font-semibold text-green-600 border-0 border-b border-dashed rounded-none pl-3 pr-0 focus-visible:ring-0"
                              placeholder={buyPriceSame ? String(existSellPrice) : (bn ? 'নতুন দাম' : 'New price')}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Category comparison (only show if different) */}
                      {categoryDiffers && (
                        <div className="p-1.5 bg-orange-50 rounded border border-orange-200">
                          <p className="text-[9px] uppercase text-slate-500 mb-0.5">{bn ? 'ক্যাটাগরি পরিবর্তন' : 'Category differs'}</p>
                          <div className="flex items-center gap-2 text-[10px] sm:text-xs">
                            <span className="capitalize text-slate-500">{existingPart.category}</span>
                            <span className="text-orange-400">→</span>
                            <Select
                              value={fields.category || ''}
                              onValueChange={v => onUpdateField(item.id, 'category', v)}
                            >
                              <SelectTrigger className="h-5 w-auto min-w-[70px] text-[10px] capitalize border-orange-300 bg-white">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {categories.map(c => (
                                  <SelectItem key={c.value} value={c.value} className="text-xs capitalize">{bn ? c.labelBn : c.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          )}

          {/* Row 1: Name + Category badge (matching inventory card header) */}
          <div className="flex items-start justify-between mb-1 sm:mb-2">
            <div className="min-w-0 flex-1 mr-2">
              <Input
                value={fields.name || ''}
                onChange={e => onUpdateField(item.id, 'name', e.target.value)}
                className={`h-8 sm:h-9 text-sm sm:text-base font-semibold text-slate-900 border-0 border-b border-dashed border-slate-300 rounded-none px-0 focus-visible:ring-0 focus-visible:border-blue-500 ${!fields.name ? 'border-red-300' : ''}`}
                placeholder={bn ? 'পার্টের নাম' : 'Part Name'}
              />
            </div>
            <Select
              value={fields.category || ''}
              onValueChange={v => onUpdateField(item.id, 'category', v)}
            >
              <SelectTrigger className={`h-6 sm:h-7 w-auto min-w-[80px] text-[10px] sm:text-xs capitalize border-0 shrink-0 ${!fields.category ? 'bg-red-50 text-red-600 ring-1 ring-red-300' : 'bg-orange-400 text-white'}`}>
                <SelectValue placeholder={bn ? 'ক্যাটাগরি' : 'Category'} />
              </SelectTrigger>
              <SelectContent>
                {categories.map(c => (
                  <SelectItem key={c.value} value={c.value} className="text-xs capitalize">{bn ? c.labelBn : c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Row 2: SKU + Brand (always editable) */}
          <div className="flex items-center gap-2 mb-2 sm:mb-3">
            <Input
              value={fields.sku || ''}
              onChange={e => onUpdateField(item.id, 'sku', e.target.value)}
              className="h-6 text-xs sm:text-sm border-0 border-b border-dashed border-slate-200 rounded-none px-0 focus-visible:ring-0 focus-visible:border-blue-500 w-20 sm:w-24"
              placeholder="SKU"
            />
            <span className="text-slate-300">|</span>
            <div className="flex items-center gap-1 flex-1">
              <span className="text-[10px] sm:text-xs text-slate-400 shrink-0">{bn ? 'ব্র্যান্ড' : 'Brand'}:</span>
              <Input
                value={fields.brand || ''}
                onChange={e => onUpdateField(item.id, 'brand', e.target.value)}
                className="h-6 text-xs sm:text-sm border-0 border-b border-dashed border-slate-200 rounded-none px-0 focus-visible:ring-0 focus-visible:border-blue-500"
                placeholder={bn ? 'ব্র্যান্ড লিখুন' : 'Enter brand'}
              />
            </div>
          </div>

          {/* Row 3: Price grid (hidden in update-stock mode since comparison view has these) */}
          {!(updateExisting && existingPart) && (
          <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-3 sm:mb-4">
            <div>
              <p className="text-[10px] sm:text-xs text-slate-500 flex items-center">
                {bn ? 'কেনা দাম' : 'Purchase Price'}{confidenceDot('purchase_price')}
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-500 ml-1" />
              </p>
              <div className="relative">
                <span className="absolute left-0 top-1/2 -translate-y-1/2 text-sm text-slate-400">৳</span>
                <Input
                  type="number"
                  value={fields.purchase_price || ''}
                  onChange={e => onUpdateField(item.id, 'purchase_price', parseFloat(e.target.value) || 0)}
                  className={`h-8 sm:h-9 text-sm sm:text-base font-semibold border-0 border-b border-dashed border-slate-300 rounded-none pl-4 pr-0 focus-visible:ring-0 focus-visible:border-blue-500 ${!fields.purchase_price ? 'border-red-300' : ''}`}
                />
              </div>
            </div>
            <div>
              <p className="text-[10px] sm:text-xs text-slate-500">
                {bn ? 'বিক্রি দাম' : 'Selling Price'}<span className="text-red-500 ml-0.5">*</span>
              </p>
              <div className="relative">
                <span className="absolute left-0 top-1/2 -translate-y-1/2 text-sm text-green-500">৳</span>
                <Input
                  ref={sellingPriceRef}
                  type="number"
                  value={fields.selling_price || ''}
                  onChange={e => onUpdateField(item.id, 'selling_price', parseFloat(e.target.value) || 0)}
                  onKeyDown={handleSellingPriceKeyDown}
                  className={`h-8 sm:h-9 text-sm sm:text-base font-semibold text-green-600 border-0 border-b border-dashed rounded-none pl-4 pr-0 focus-visible:ring-0 focus-visible:border-green-500 ${!fields.selling_price ? 'border-amber-400 bg-amber-50/50' : 'border-green-300'}`}
                  placeholder={bn ? 'আপনার দাম' : 'Your price'}
                />
              </div>
            </div>
          </div>
          )}

          {/* Row 4: Stock + Margin bar (hidden in update-stock mode) */}
          {!(updateExisting && existingPart) && (
          <div className="flex items-center justify-between p-2 sm:p-3 bg-slate-100 rounded-lg mb-2 sm:mb-3">
            <div>
              <p className="text-[10px] sm:text-xs text-slate-500">{bn ? 'স্টক' : 'Stock'}</p>
              <div className="flex items-center gap-1">
                <Input
                  type="number"
                  value={fields.stock_quantity || ''}
                  onChange={e => onUpdateField(item.id, 'stock_quantity', parseInt(e.target.value) || 0)}
                  className="h-7 w-16 text-lg sm:text-2xl font-bold border-0 bg-transparent px-0 focus-visible:ring-0"
                />
              </div>
            </div>
            <div className="text-right">
              <p className="text-[10px] sm:text-xs text-slate-500">{bn ? 'মার্জিন' : 'Margin'}</p>
              <span className={`font-bold text-sm sm:text-base ${profit > 0 ? 'text-green-600' : profit < 0 ? 'text-red-600' : 'text-slate-400'}`}>
                {fields.selling_price ? `${profitMargin}%` : '—'}
              </span>
            </div>
          </div>
          )}

          {/* Row 5: Consumable Category (if reminders enabled) */}
          {remindersEnabled && consumableCategories?.length > 0 && (
            <div className="mb-2 sm:mb-3 border-t pt-2 sm:pt-3">
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-[10px] sm:text-xs text-slate-500">{bn ? 'কনজিউমেবল ক্যাটাগরি' : 'Consumable Category'}</p>
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] sm:text-xs text-slate-400">{bn ? 'কনজিউমেবল নয়' : 'Not consumable'}</span>
                  <Switch
                    checked={!isConsumable}
                    onCheckedChange={(v) => {
                      setIsConsumable(!v);
                      if (v) {
                        const notConsumCat = consumableCategories.find(c => c.is_non_consumable || c.name?.toLowerCase() === 'not consumable');
                        setConsumableCategoryId(notConsumCat ? notConsumCat.id : '');
                        setConsumableSuggest(null);
                      } else {
                        setConsumableCategoryId('');
                        setConsumableSuggest(null);
                      }
                    }}
                    className="scale-75"
                  />
                </div>
              </div>
              {isConsumable && (
                <>
                  <Select value={consumableCategoryId} onValueChange={setConsumableCategoryId}>
                    <SelectTrigger className="h-7 sm:h-8 text-xs sm:text-sm">
                      <SelectValue placeholder={bn ? 'ক্যাটাগরি নির্বাচন করুন...' : 'Select category...'} />
                    </SelectTrigger>
                    <SelectContent>
                      {consumableCategories.map(c => (
                        <SelectItem key={c.id} value={c.id} className="text-xs">{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {consumableSuggest && consumableCategoryId === consumableSuggest.suggested_category_id && (
                    <p className="text-[10px] text-slate-400 mt-0.5">{bn ? 'স্বয়ংক্রিয়ভাবে নির্বাচিত' : 'Auto-suggested'}</p>
                  )}
                </>
              )}
            </div>
          )}

          {/* Raw text toggle */}
          <button
            className="flex items-center gap-1 text-[10px] sm:text-xs text-slate-400 hover:text-slate-600 transition-colors mb-2"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            {bn ? 'মূল টেক্সট দেখুন' : 'View original text'}
          </button>
          <AnimatePresence>
            {expanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden mb-2"
              >
                <div className="bg-slate-50 rounded p-2 text-[10px] sm:text-xs text-slate-600 font-mono whitespace-pre-wrap max-h-24 overflow-y-auto">
                  {item.raw_text || '—'}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Action buttons (matching inventory card button row) */}
          <div className="flex gap-1 sm:gap-2">
            <Button
              ref={addBtnRef}
              className="flex-1 text-xs sm:text-sm h-8 sm:h-9 gap-1.5 bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
              onClick={handleApprove}
              onKeyDown={handleAddBtnKeyDown}
              disabled={!hasRequired || approving}
            >
              {approving ? <Spinner size="sm" /> : <Check className="w-3.5 h-3.5" />}
              {updateExisting ? (bn ? 'আপডেট করুন' : 'Update Stock') : (bn ? 'যোগ করুন' : 'Add')}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 sm:h-9 w-8 sm:w-9 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={handleReject}
              disabled={rejecting}
            >
              {rejecting ? <Spinner size="sm" /> : <X className="w-3.5 h-3.5" />}
            </Button>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
